"use client";
import React, { useMemo } from "react";
import useSWR from "swr";
import {
  buildScanToOrderSearchParams,
  extractScanToOrderParams,
} from "@/utils/scanToOrderUtils";
import { useTranslations } from "next-intl";
import { Menu } from "@/dto/menu";
import { Merchant } from "@/dto/merchant";
import { OrderType } from "@/constants/enum";
import { MenuCategoriesList } from "@/components/menu/menuCategoriesList";
import { getRestaurantMenuAction } from "../actions";

interface RestaurantMenuItemsProps {
  menu: Menu | null;
  merchant: Merchant;
  merchantId: string;
  menuId?: string;
  searchParams: { [key: string]: string | string[] | undefined };
  itemTemplate?: string;
  query?: string;
}

export const RestaurantMenuItems = ({
  menu,
  merchant,
  merchantId,
  menuId,
  searchParams,
  itemTemplate,
  query,
}: RestaurantMenuItemsProps) => {
  const swrKey = JSON.stringify({ order_type: searchParams.order_type, menu: searchParams.menu });
  const { data } = useSWR(
    ["restaurantMenu", merchantId, swrKey],
    () => getRestaurantMenuAction(merchantId, searchParams, menuId),
    {
      fallbackData: { menu },
      revalidateOnMount: true,
      revalidateOnFocus: false,
    }
  );
  const activeMenu = data?.menu ?? menu;
  const { name: merchantName } = merchant;
  const tRestaurant = useTranslations("restaurantMenu");

  const queryParams = useMemo(() => {
    const { menuName, orderType, tableId, employeeId } =
      extractScanToOrderParams(searchParams);

    let validOrderType: OrderType | undefined = undefined;
    if (
      orderType &&
      Object.values(OrderType).includes(orderType.toUpperCase() as OrderType)
    ) {
      validOrderType = orderType.toUpperCase() as OrderType;
    }

    let params = "";
    if (orderType || tableId || employeeId) {
      params = buildScanToOrderSearchParams({
        orderType: validOrderType,
        tableId: tableId ?? undefined,
        employeeId: employeeId ?? undefined,
      });
    }

    if (menuName) {
      params += params ? `&menu=${menuName}` : `?menu=${menuName}`;
    }
    return params;
  }, [searchParams]);

  const getBaseRoute = (isEssential: boolean) =>
    isEssential ? "/categories" : "/menu";

  const baseRoute = getBaseRoute(merchant.isEssentialMerchant);

  if (!activeMenu) return null;

  return (
    <MenuCategoriesList
      menu={activeMenu}
      merchantName={merchantName}
      itemTemplate={itemTemplate}
      query={query ?? ""}
      baseRoute={baseRoute}
      queryParams={queryParams}
      uncategorizedLabel={tRestaurant("uncategorized")}
    />
  );
};
